import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {
  woundsizeStudioConfig,
  woundtypeStudioConfig,
} from '@modules/forms/form-modules/woundtrack/config/woundtrack-studio.config';
import { ComplianceLogoComponent } from '@modules/forms/form-modules/woundtrack/modules/compliance-logo/compliance-logo.component';
import { WoundsizeService } from '@modules/forms/form-modules/woundtrack/services/woundsize.service';
import { WoundtrackExportService } from '@modules/forms/form-modules/woundtrack/services/woundtrack-export.service';
import { WoundtypeService } from '@modules/forms/form-modules/woundtrack/services/woundtype.service';
import {
  getWoundsizeFileName,
  getWoundtrackFileName,
  getWoundtypeFileName,
} from '@modules/forms/form-modules/woundtrack/utils/woundtrack-export.utils';
import { ModuleResultComponent } from '@modules/shared/module-result/module-result.component';
import { TopBarConfig } from '@modules/shared/top-bar/top-bar-config';
import { StudioConfig } from '@modules/studio/models/studio-config';
import { StudioEventService } from '@modules/studio/studio/studio-event.service';
import { StudioExportService } from '@modules/studio/studio/studio-export.service';
import { StudioMetadataService } from '@modules/studio/studio/studio-metadata.service';
import { StudioComponent } from '@modules/studio/studio/studio.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EventTrackerService } from '@shared/event-tracker/services/event-tracker.service';
import { MenuActionId } from '@shared/models/menu-actions/menu-action-id.enum';
import { base64ToFile, blobToBase64, fetchBlob } from '@shared/utils/utils';
import { TuiDialogContext, TuiNotificationService } from '@taiga-ui/core';
import { TuiCardLarge } from '@taiga-ui/layout';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import {
  BehaviorSubject,
  combineLatest,
  delay,
  filter,
  first,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { WoundtrackStep } from '../../enums/woundtrack-step.enum';
import {
  WoundtrackModuleComponentInputs,
  WoundtrackModuleComponentOutputs,
} from '../../models/woundtrack-module-component';
import { WoundtrackService } from '../../services/woundtrack.service';
import { ScanLoaderComponent } from '../scan-loader/scan-loader.component';

type WoundtrackContext = TuiDialogContext<WoundtrackModuleComponentOutputs, WoundtrackModuleComponentInputs>;
@UntilDestroy()
@Component({
  selector: 'pxc-woundtrack',
  imports: [
    CommonModule,
    StudioComponent,
    TuiCardLarge,
    ModuleResultComponent,
    TranslocoDirective,
    ScanLoaderComponent,
    ComplianceLogoComponent,
  ],
  providers: [
    StudioEventService,
    StudioExportService,
    StudioMetadataService,
    WoundsizeService,
    WoundtypeService,
    WoundtrackExportService,
  ],
  host: {
    class: 'block h-screen',
  },
  templateUrl: './woundtrack.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WoundtrackComponent implements OnInit {
  private readonly translocoService = inject(TranslocoService);
  private readonly woundsizeService = inject(WoundsizeService);
  private readonly woundtypeService = inject(WoundtypeService);
  private readonly woundtrackService = inject(WoundtrackService);
  private readonly woundtrackExportService = inject(WoundtrackExportService);
  private readonly eventTracker = inject(EventTrackerService);
  private readonly alertService = inject(TuiNotificationService);
  private readonly context = inject<WoundtrackContext>(POLYMORPHEUS_CONTEXT);

  private readonly woundsizeShapes$ = this.woundsizeService.woundsizeShapes$;
  private readonly areWoundsizeButtonsDisabled$ = this.woundsizeService.areWoundsizeButtonsDisabled$;
  private readonly woundsizeDisabledHint$ = this.woundsizeService.woundsizeDisabledHint$;

  readonly woundsizeResultItems$ = this.woundsizeService.woundsizeResultItems$;
  readonly isWoundsizeLoading$ = this.woundsizeService.isWoundsizeLoading$;

  readonly woundtypeResultItems$ = this.woundtypeService.woundtypeResultItems$;
  readonly isWoundtypeLoading$ = this.woundtypeService.isWoundtypeLoading$;
  private readonly areWoundtypeButtonsDisabled$ = combineLatest([this.woundsizeShapes$, this.isWoundtypeLoading$]).pipe(
    map(([woundsizeShapes, isWoundtypeLoading]) => !woundsizeShapes || isWoundtypeLoading),
    startWith(true),
  );

  private readonly _currentStep$ = new BehaviorSubject<WoundtrackStep>(WoundtrackStep.WOUNDSIZE);
  readonly currentStep$ = this._currentStep$.asObservable();

  readonly WoundtrackStep = WoundtrackStep;

  targetModuleId: WoundtrackModuleComponentInputs['targetModuleId'] = this.context.data?.targetModuleId;
  picture: WoundtrackModuleComponentInputs['picture'] = this.context.data?.picture;
  isNewPicture: WoundtrackModuleComponentInputs['isNewPicture'] = this.context.data?.isNewPicture;

  get woundtrackPicture$(): Observable<string> {
    return fetchBlob(this.picture.source).pipe(
      switchMap((blob) => blobToBase64(blob)),
      untilDestroyed(this),
    );
  }

  get woundtrackResultPicture$(): Observable<string> {
    return this.woundtrackExportService.getExportPicture(this.targetModuleId);
  }

  get woundsizeStudioConfig(): StudioConfig {
    return { ...woundsizeStudioConfig };
  }

  get woundsizeBarConfig(): TopBarConfig {
    return {
      title: this.translocoService.translate('Analyse dimensionnelle'),
      leftActions: [
        {
          id: MenuActionId.BACK,
          hint: this.translocoService.translate('Retour'),
          icon: '@tui.arrow-left',
          execute: (): void => {
            this.context.completeWith(null);
          },
        },
        {
          id: MenuActionId.STUDIO_DOWNLOAD,
          hint: this.translocoService.translate('Télécharger la photo'),
          icon: '@tui.download',
          execute: (): void => {
            this.woundsizeService.downloadPicture();
          },
        },
      ],
      leftButtons: [
        {
          icon: '@tui.arrow-left',
          hint: this.translocoService.translate('Retour'),
          actionIds: [MenuActionId.BACK],
        },
        {
          icon: '@tui.download',
          iconOnly: true,
          hint: this.translocoService.translate('Télécharger la photo'),
          disabled$: this.areWoundsizeButtonsDisabled$,
          actionIds: [MenuActionId.STUDIO_DOWNLOAD],
        },
      ],
      rightActions: [
        {
          id: MenuActionId.CANCEL,
          label: this.translocoService.translate('Annuler'),
          execute: (): void => {
            this.context.completeWith(null);
          },
        },
        {
          id: MenuActionId.COMPLETE,
          label: this.translocoService.translate('Terminer'),
          execute: (): void => {
            this.completeWoundtrack(getWoundsizeFileName());
          },
        },
        {
          id: MenuActionId.NEXT,
          label: this.translocoService.translate('Analyse tissulaire'),
          execute: (): void => {
            this.goToWoundtypeStep();
          },
        },
      ],
      rightButtons: [
        {
          icon: '@tui.circle-help',
          iconOnly: true,
          hint: this.translocoService.translate('Aide'),
          actionIds: [MenuActionId.HELP],
        },
        {
          title: this.translocoService.translate('Annuler'),
          appearance: 'outline-destructive',
          actionIds: [MenuActionId.CANCEL],
        },
        {
          title: this.translocoService.translate('Terminer'),
          appearance: 'secondary',
          disabled$: this.areWoundsizeButtonsDisabled$,
          hintOnDisabled$: this.woundsizeDisabledHint$,
          actionIds: [MenuActionId.COMPLETE],
        },
        {
          title: this.translocoService.translate('Analyse tissulaire'),
          appearance: 'primary',
          disabled$: this.areWoundsizeButtonsDisabled$,
          hintOnDisabled$: this.woundsizeDisabledHint$,
          iconEnd: '@tui.arrow-right',
          actionIds: [MenuActionId.NEXT],
        },
      ],
    };
  }

  get woundtypeStudioConfig(): StudioConfig {
    return { ...woundtypeStudioConfig };
  }

  get woundtypeBarConfig(): TopBarConfig {
    return {
      title: this.translocoService.translate('Analyse tissulaire'),
      leftActions: [
        {
          id: MenuActionId.BACK,
          hint: this.translocoService.translate('Retour'),
          icon: '@tui.arrow-left',
          execute: (): void => {
            this.goToWoundsizeStep();
          },
        },
        {
          id: MenuActionId.STUDIO_DOWNLOAD,
          hint: this.translocoService.translate('Télécharger la photo'),
          icon: '@tui.download',
          execute: (): void => {
            this.woundtypeService.downloadPicture();
          },
        },
      ],
      leftButtons: [
        {
          icon: '@tui.arrow-left',
          hint: this.translocoService.translate('Retour'),
          disabled$: this.areWoundtypeButtonsDisabled$,
          actionIds: [MenuActionId.BACK],
        },
        {
          icon: '@tui.download',
          iconOnly: true,
          hint: this.translocoService.translate('Télécharger la photo'),
          disabled$: this.areWoundtypeButtonsDisabled$,
          actionIds: [MenuActionId.STUDIO_DOWNLOAD],
        },
      ],
      rightActions: [
        {
          id: MenuActionId.CANCEL,
          label: this.translocoService.translate('Annuler'),
          icon: '@tui.arrow-left',
          execute: (): void => {
            this.context.completeWith(null);
          },
        },
        {
          id: MenuActionId.COMPLETE,
          label: this.translocoService.translate('Terminer'),
          icon: '@tui.check',
          execute: (): void => {
            this.completeWoundtrack(getWoundtypeFileName());
          },
        },
      ],
      rightButtons: [
        {
          icon: '@tui.circle-help',
          iconOnly: true,
          hint: this.translocoService.translate('Aide'),
          actionIds: [MenuActionId.HELP],
        },
        {
          title: this.translocoService.translate('Annuler'),
          appearance: 'outline-destructive',
          actionIds: [MenuActionId.CANCEL],
        },
        {
          title: this.translocoService.translate('Terminer'),
          appearance: 'primary',
          disabled$: this.areWoundtypeButtonsDisabled$,
          hintOnDisabled$: of(this.translocoService.translate<string>('Analyse en cours, veuillez patienter')),
          actionIds: [MenuActionId.COMPLETE],
        },
      ],
    };
  }

  ngOnInit(): void {
    if (this.picture?.arucoSurface) {
      this.woundsizeService.setWoundsizeWorker(this.picture.arucoSurface);
      this.getWoundsizePrediction();
    }
  }

  private getWoundsizePrediction(): void {
    if (!this.picture) {
      return;
    }

    this.woundtrackService
      .getWoundsizeResult(this.picture.source)
      .pipe(
        tap((polygon) => {
          if (!polygon?.points.length) {
            this.woundsizeService.stopWoundsizeLoading();
            this.alertService
              .open(this.translocoService.translate('Veuillez procéder à un détourage manuel de la lésion.'), {
                label: this.translocoService.translate('Aucune lésion détectée'),
                appearance: 'negative',
              })
              .subscribe();
          } else {
            this.woundsizeService.drawPredictedWoundsize(polygon);
          }
        }),
      )
      .subscribe();
  }

  private completeWoundtrack(resultPictureFileName: string): void {
    combineLatest([
      this.woundsizeService.woundsizeResult$,
      this.woundtypeService.woundtypeResult$,
      this.woundtrackPicture$,
      this.woundtrackResultPicture$,
    ])
      .pipe(
        first(),
        tap(() => this.eventTracker.trackWoundtrackII(this.targetModuleId, this.picture)),
      )
      .subscribe(([woundsizeResult, woundtypeResult, woundtrackPicture, resultPicture]) => {
        this.context.completeWith({
          ...woundsizeResult,
          ...woundtypeResult,
          pictures: [
            ...(this.isNewPicture ? [base64ToFile(woundtrackPicture, getWoundtrackFileName())] : []),
            base64ToFile(resultPicture, resultPictureFileName),
          ],
        });
      });
  }

  private goToWoundsizeStep(): void {
    this._currentStep$.next(WoundtrackStep.WOUNDSIZE);
    this.woundsizeShapes$
      .pipe(
        filter((shapes) => !!shapes?.polygon?.points?.length),
        delay(500),
        first(),
        tap(() => {
          this.woundtypeService.resetWoundtype();
          this.woundsizeService.redrawWoundsize();
        }),
      )
      .subscribe();
  }

  private goToWoundtypeStep(): void {
    this._currentStep$.next(WoundtrackStep.WOUNDTYPE);
    this.woundsizeShapes$
      .pipe(
        filter((shapes) => !!shapes?.polygon?.points?.length),
        first(),
        switchMap(({ polygon }) =>
          this.woundtrackService.getWoundtypeResult(this.picture.source, polygon.points).pipe(
            tap(({ segmentation, classProportions }) => {
              this.woundtypeService.drawWoundtype(segmentation, classProportions);
            }),
          ),
        ),
      )
      .subscribe();
  }
}
